import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import LoginModal from "../auth/LoginModal";
import SignupModal from "../auth/SignupModal";
import WishlistPage from "./WishlistPage";
import TripHistoryOverlay from "./TripHistoryOverlay";
import "bootstrap/dist/js/bootstrap.bundle.min";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../styles/site.css";

const NavBar = ({ categories = [] }) => {
  const navRef = useRef(null);
  const collapseRef = useRef(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [showWishlist, setShowWishlist] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [openCategoryId, setOpenCategoryId] = useState(null);
  const navigate = useNavigate();

  const openLoginModal = () => setShowLogin(true);
  const closeLoginModal = () => setShowLogin(false);
  const openSignupModal = () => setShowSignup(true);
  const closeSignupModal = () => setShowSignup(false);
  const [showTripHistory, setShowTripHistory] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('io-theme') || 'auto');
  const [systemDark, setSystemDark] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)').matches : false
  );

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'auto') {
      root.removeAttribute('data-theme');
    } else {
      root.setAttribute('data-theme', theme);
    }
    localStorage.setItem('io-theme', theme);
  }, [theme]);

  // Watch system theme to decide navbar color scheme when in 'auto'
  useEffect(() => {
    if (!window.matchMedia) return;
    const mm = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemDark(e.matches);
    mm.addEventListener?.('change', handler);
    setSystemDark(mm.matches);
    return () => mm.removeEventListener?.('change', handler);
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 8);
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const closeOnOutside = (event) => {
      if (!navRef.current?.contains(event.target)) {
        setOpenCategoryId(null);
      }
    };

    document.addEventListener("pointerdown", closeOnOutside);
    return () => document.removeEventListener("pointerdown", closeOnOutside);
  }, []);

  useEffect(() => {
    const loadUser = () => {
      const storedUser = localStorage.getItem("user");
      setCurrentUser(storedUser ? JSON.parse(storedUser) : null);
    };

    loadUser(); // Load initially

    // Listen for login or logout events
    window.addEventListener("userLoggedIn", loadUser);
    return () => window.removeEventListener("userLoggedIn", loadUser);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("user");
    setCurrentUser(null);
    window.dispatchEvent(new Event("userLoggedIn")); // Trigger NavBar update
    navigate("/");
  };

  const toggleNavbar = () => {
    setOpenCategoryId(null);
    const navbar = collapseRef.current;
    if (!navbar) return;

    const bsCollapse = window.bootstrap?.Collapse.getInstance(navbar);

    if (bsCollapse) {
      bsCollapse.toggle(); // this handles both open and close
    } else {
      new window.bootstrap.Collapse(navbar, { toggle: true });
    }
  };

  const closeNavbar = () => {
    setOpenCategoryId(null);
    const navbar = collapseRef.current;
    if (!navbar) return;

    const bsCollapse = window.bootstrap?.Collapse.getInstance(navbar);
    if (bsCollapse) {
      bsCollapse.hide();
    }
  };

  const toggleCategory = (categoryId) => {
    setOpenCategoryId((current) =>
      String(current) === String(categoryId) ? null : categoryId,
    );
  };

  const isCategoryOpen = (categoryId) =>
    String(openCategoryId) === String(categoryId);

  const cycleTheme = () => {
    setTheme((t) => (t === "auto" ? "dark" : t === "dark" ? "light" : "auto"));
  };

  return (
    <>
      {(() => {
        const darkUI = theme === 'dark' || (theme === 'auto' && systemDark);
        const navSchemeClass = darkUI ? 'navbar-dark' : 'navbar-light';
        const outlineVariant = darkUI ? 'outline-light' : 'outline-dark';
        return (
          <nav
            ref={navRef}
            className={`navbar navbar-expand-lg ${navSchemeClass} sticky-top io-nav ${isScrolled ? "is-scrolled" : ""} io-blur`}
            style={{
              backdropFilter: 'saturate(160%) blur(10px)',
              zIndex: 1040,
            }}
          >
            <div className="container">
              <Link to="/" className="navbar-brand">
                <img
                  src="../img/icon.png"
                  className="img-fluid rounded-circle"
                  alt="Indian Odyssey logo"
                  width="42"
                  height="42"
                />
                <span className="io-nav-brand-text">
                  <strong>Indian Odyssey</strong>
                  <small>Trips</small>
                </span>
              </Link>

              <button
                className="navbar-toggler"
                type="button"
                aria-label="Toggle navigation"
                onClick={toggleNavbar}
              >
                <span className="navbar-toggler-icon"></span>
              </button>

              <div
                className="collapse navbar-collapse"
                id="navmenu"
                ref={collapseRef}
              >
                <ul className="navbar-nav ms-auto align-items-lg-center">
                  {(categories || []).map((category) => (
                    <li
                      key={category.id}
                      className={`nav-item dropdown ${String(openCategoryId) === String(category.id) ? "show" : ""}`}
                    >
                      <button
                        type="button"
                        className="nav-link dropdown-toggle bg-transparent border-0 text-start"
                        aria-expanded={isCategoryOpen(category.id)}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          toggleCategory(category.id);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Escape") {
                            setOpenCategoryId(null);
                          }
                        }}
                      >
                        {category.title}
                      </button>
                      <ul
                        className={`dropdown-menu nav-trip-dropdown ${isCategoryOpen(category.id) ? "show" : ""} ${darkUI ? 'bg-dark' : ''}`}
                      >
                        {groupTripsByState(category.trips).map((stateGroup, stateIndex) => (
                          <React.Fragment key={stateGroup.key}>
                            {stateIndex > 0 && <li><hr className="dropdown-divider" /></li>}
                            <li><h6 className={`dropdown-header nav-state-header ${darkUI ? "text-info" : "text-primary"}`}><span>{stateGroup.name}</span><span className="nav-state-count">{stateGroup.trips.length}</span></h6></li>
                            {stateGroup.trips.map((trip) => (
                              <li key={trip.id}>
                                <Link
                                  className={`dropdown-item ${darkUI ? 'text-white' : ''}`}
                                  to={`/trips/${trip.path}`}
                                  onClick={closeNavbar}
                                >
                                  {trip.title}
                                </Link>
                              </li>
                            ))}
                          </React.Fragment>
                        ))}
                        {(category.trips || []).length > 5 && (
                          <li className={`nav-scroll-hint ${darkUI ? "nav-scroll-hint-dark" : ""}`} aria-hidden="true">
                            Swipe up for more trips <span>↓</span>
                          </li>
                        )}
                      </ul>
                    </li>
                  ))}

                  <li className="nav-item d-flex align-items-center ms-lg-3 gap-2 io-nav-actions">
                    {/* Theme toggle */}
                    <div className="d-flex align-items-center me-1">
                      <button
                        type="button"
                        className="io-nav-icon-btn"
                        title={`Theme: ${theme}`}
                        aria-label={`Theme: ${theme}`}
                        onPointerUp={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          cycleTheme();
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            cycleTheme();
                          }
                        }}
                        style={{ touchAction: "manipulation" }}
                      >
                        <i
                          className={`bi ${theme === 'dark' ? 'bi-moon-stars' : theme === 'light' ? 'bi-sun' : 'bi-circle-half'}`}
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                    {currentUser ? (
                      <>
                        <span className="me-1 io-nav-user">
                          <i className="bi bi-person-circle me-1" aria-hidden="true" />
                          {currentUser.name || currentUser.email}
                        </span>
                        <button
                          onClick={() => setShowWishlist(true)}
                          className={`btn btn-${outlineVariant} btn-sm`}
                        >
                          My Wishlist
                        </button>
                        <button
                          onClick={() => setShowTripHistory(true)}
                          className={`btn btn-${outlineVariant} btn-sm`}
                        >
                          My Bookings
                        </button>
                        <button
                          onClick={() => setShowTripHistory(true)}
                          className={`btn btn-${outlineVariant} btn-sm`}
                        >
                          Trip Tracking
                        </button>

                        <button
                          onClick={handleLogout}
                          className={`btn btn-${outlineVariant} btn-sm`}
                        >
                          Logout
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={openLoginModal}
                          className="io-nav-login"
                        >
                          <i className="bi bi-person" aria-hidden="true" />
                          Login
                        </button>
                      </>
                    )}
                  </li>
                </ul>
              </div>
            </div>
          </nav>
        );
      })()}

      <style>
        {`
          .nav-trip-dropdown {
            max-height: 360px;
            min-width: 260px;
            overflow-y: auto;
          }
          .nav-state-header {
            align-items: center;
            display: flex;
            font-size: 0.72rem;
            font-weight: 700;
            justify-content: space-between;
            letter-spacing: 0.05em;
            text-transform: uppercase;
          }
          .nav-state-count {
            align-items: center;
            background: rgba(13, 110, 253, 0.12);
            border-radius: 999px;
            display: inline-flex;
            font-size: 0.68rem;
            height: 1.35rem;
            justify-content: center;
            letter-spacing: 0;
            min-width: 1.35rem;
          }
          .nav-scroll-hint {
            display: none;
          }
          @media (max-width: 991.98px) {
            .nav-trip-dropdown {
              border: 1px solid rgba(13, 110, 253, 0.28);
              border-radius: 0.75rem;
              box-shadow: 0 0.5rem 1.25rem rgba(15, 23, 42, 0.14);
              margin: 0.35rem 0 0.75rem;
              max-height: min(58vh, 420px);
              padding: 0.4rem;
              scrollbar-color: #0d6efd transparent;
              scrollbar-width: thin;
            }
            .nav-trip-dropdown .dropdown-item {
              border-radius: 0.45rem;
              padding: 0.62rem 0.8rem 0.62rem 1rem;
              white-space: normal;
            }
            .nav-trip-dropdown .nav-state-header {
              background: rgba(13, 110, 253, 0.08);
              border-radius: 0.45rem;
              margin: 0.2rem 0;
              padding: 0.55rem 0.7rem;
            }
            .nav-scroll-hint {
              background: rgba(255, 255, 255, 0.96);
              bottom: -0.4rem;
              color: #0d6efd;
              display: flex;
              font-size: 0.75rem;
              font-weight: 700;
              justify-content: center;
              letter-spacing: 0.02em;
              margin: 0 -0.4rem -0.4rem;
              padding: 0.55rem;
              position: sticky;
              text-transform: uppercase;
              z-index: 2;
            }
            .nav-scroll-hint span {
              margin-left: 0.35rem;
            }
            .nav-scroll-hint-dark {
              background: rgba(33, 37, 41, 0.97);
              color: #6edff6;
            }
          }
        `}
      </style>

      <LoginModal
        show={showLogin}
        handleClose={closeLoginModal}
        openSignupModal={openSignupModal}
        closeLoginModal={closeLoginModal}
      />
      <SignupModal
        show={showSignup}
        handleClose={closeSignupModal}
        openLoginModal={openLoginModal}
        closeSignupModal={closeSignupModal}
      />

      {showWishlist && <WishlistPage onClose={() => setShowWishlist(false)} />}
      {showTripHistory && (
        <TripHistoryOverlay onClose={() => setShowTripHistory(false)} />
      )}
    </>
  );
};

function groupTripsByState(trips = []) {
  const groups = new Map();
  [...trips]
    .sort((a, b) =>
      Number(!a.state?.trim()) - Number(!b.state?.trim()) ||
      (a.state || "").localeCompare(b.state || "") ||
      (a.displayOrder ?? Number.MAX_SAFE_INTEGER) - (b.displayOrder ?? Number.MAX_SAFE_INTEGER) ||
      (a.id ?? 0) - (b.id ?? 0))
    .forEach((trip) => {
      const name = trip.state?.trim() || "Other";
      const key = name.toLowerCase();
      if (!groups.has(key)) groups.set(key, { key, name, trips: [] });
      groups.get(key).trips.push(trip);
    });
  return [...groups.values()];
}

export default NavBar;
